import { Box, SelectChangeEvent, Theme, Typography, useTheme } from "@mui/material";
import { CommonLayout } from "../../commonComponents";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { RootStateType } from "../../../redux-store/reducers";
import { isModuleAllowed } from "../../../utils/utilityFunctions";
import BreadCrum from "../../../lib/BreadCrum";
import { useRef, useState } from "react";
import { PaginationObj } from "../../investorOverview/types";
import { Form, Formik, FormikProps } from "formik";
import * as Yup from 'yup';
import MFSelectField from "../../../lib/formik/SelectField";
import { MFSubmitButton } from "../../../lib/formik";
import { FamilyFeeSummaryObj, FamilyObj, GetFamilyFeeSummaryResponseBody, GetReportFamiliesListResponseBody } from "../../../redux-store/types/onBoarding";
import { getFiscalYearOptions, getQuartersForFiscalYear } from "../financialYear";
import FormikSearchDropdown from "../../../lib/formik/FormikSearchDropdown";
import { getFamilyFeeSummary, getReportFamiliesList } from "../../../redux-store/actions/reports";
import { showError } from "../../../redux-store/actions/auth";
import NoData from "../NoData";
import RiaSummaryTable from "./RiaSummaryTable";

interface ModifiedFamilyObj extends FamilyObj {
    label: string;
    value: string;
}

interface FilterValues {
    year: string;
    quarter: string;
    family: ModifiedFamilyObj | null;
}

const initialFilterValues: FilterValues = {
    year: "",
    quarter: "",
    family: null
}

const filterValidationSchema = Yup.object().shape({
    year: Yup
        .string()
        .required('Please select Year'),
    quarter: Yup
        .string()
        .required('Please select Quarter'),
    family: Yup
        .object()
        .nullable()
        .test(
            "family-required",
            "Please select Family",
            function (value) { return !!value }
        ),
})

const YEARS = getFiscalYearOptions()

const FILTER_FIELD_HEIGHT = "56px";
const FILTER_ERROR_GAP = "6px";
const FILTER_ERROR_SLOT_HEIGHT = "22px";
const FILTER_COLUMN_HEIGHT = `calc(${FILTER_FIELD_HEIGHT} + ${FILTER_ERROR_GAP} + ${FILTER_ERROR_SLOT_HEIGHT})`;

const filterInputHeightSx = {
    "& .MuiInputBase-root": {
        height: FILTER_FIELD_HEIGHT,
        minHeight: FILTER_FIELD_HEIGHT,
        maxHeight: FILTER_FIELD_HEIGHT,
        boxSizing: "border-box",
    },
    "& .MuiSelect-select": {
        padding: "15px",
        height: "100%",
        minHeight: "unset",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-root": {
        height: FILTER_FIELD_HEIGHT,
        minHeight: FILTER_FIELD_HEIGHT,
        maxHeight: FILTER_FIELD_HEIGHT,
        padding: "0 9px",
        alignItems: "center",
        boxSizing: "border-box",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-input": {
        padding: "15px 6px",
    },
};

const filterErrorSx = {
    position: "absolute",
    left: 0,
    top: `calc(${FILTER_FIELD_HEIGHT} + ${FILTER_ERROR_GAP})`,
    margin: 0,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
};

const filterFieldWrapperSx = {
    flex: { xs: "1 1 100%", lg: "0 1 auto" },
    minWidth: { xs: "100%", lg: "145px" },
    maxWidth: { xs: "100%", lg: "205px" },
    width: { xs: "100%", lg: "auto" },
    position: "relative",
    minHeight: FILTER_COLUMN_HEIGHT,
    "& .MuiFormControl-root": {
        my: 0,
        paddingBottom: 0,
    },
    "& .MuiFormHelperText-root": filterErrorSx,
    ...filterInputHeightSx,
};

const filterQuarterWrapperSx = {
    ...filterFieldWrapperSx,
    minWidth: { xs: "100%", lg: "240px" },
    maxWidth: { xs: "100%", lg: "280px" },
};

const filterFamilyWrapperSx = {
    ...filterFieldWrapperSx,
    minWidth: { xs: "100%", lg: "300px" },
    maxWidth: { xs: "100%", lg: "350px" },
};

const filterSubmitWrapperSx = {
    flex: { xs: "1 1 100%", lg: "0 0 auto" },
    position: "relative",
    minHeight: { xs: "auto", lg: FILTER_COLUMN_HEIGHT },
    display: "flex",
    alignItems: "flex-start",
    width: { xs: "100%", lg: "auto" },
    marginTop: { xs: "1rem", lg: 0 }
};

const filterSubmitButtonSx = (theme: Theme) => ({
    ...theme.typography.body_500,
    fontWeight: 600,
    height: { xs: "auto", lg: FILTER_FIELD_HEIGHT },
    minHeight: { xs: "42px", lg: FILTER_FIELD_HEIGHT },
    maxHeight: { xs: "48px", lg: FILTER_FIELD_HEIGHT },
    py: { xs: "6px", sm: "8px", lg: 0 },
    px: { xs: "16px", sm: "20px", lg: "20px" },
    lineHeight: 1.2,
    boxSizing: "border-box",
    whiteSpace: "nowrap",
    width: { xs: "100%", lg: "auto" },
    minWidth: { xs: "unset", lg: "fit-content" },
    flexShrink: 0,
    "& .MuiCircularProgress-root": {
        color: theme.palette.text.primary,
    },
});

export default function RiaSummary(): JSX.Element {
    const theme = useTheme()
    const dispatch = useDispatch()

    const history = useHistory();
    const { pathname: pathName } = history.location
    const initialRouter = pathName.split('/').filter((item) => item !== '')

    // Data from Redux
    const { permissions = [] } = useSelector((store: RootStateType) => store.auth);
    const { iwappRmId } = useSelector((state: RootStateType) => state.id);

    // State
    const [familiesList, setFamiliesList] = useState<Array<ModifiedFamilyObj>>([])
    const [areFiltersApplied, setAreFiltersApplied] = useState<boolean>(false);
    const [loadingStatus, setLoadingStatus] = useState<string>("")
    const [tableData, setTableData] = useState<Array<FamilyFeeSummaryObj>>([])
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 5,
        totalRows: 0,
    })

    const routerPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
        {
            name: 'Dashboard',
            path: '/dashboard',
            textSx: { paddingLeft: "0px" }
        },
        {
            name: 'RIA Summary',
            path: `/${initialRouter[0]}`,
            isTextLowerCase: false
        }
    ] : [])

    const filterFormikRef = useRef<FormikProps<FilterValues>>(null);

    const handleYearChange = (event: SelectChangeEvent<unknown>) => {
        if (!filterFormikRef?.current) {
            return
        }

        const { values, setValues } = filterFormikRef.current

        setValues({
            ...values,
            year: event.target.value as string,
            quarter: ""
        })
    }

    const fetchFamiliesData = async (searchInput: string) => {
        try {
            if (`${searchInput ?? ""}`.trim().length !== 0) {

                const payload = {
                    rmId: `${iwappRmId}`,
                    searchInput: `${searchInput ?? ""}`.trim()
                }
                const response = await (dispatch(getReportFamiliesList(payload)) as unknown as Promise<GetReportFamiliesListResponseBody>);

                if (!response?.isSuccess) {
                    dispatch(showError(response?.message || "Data not found"))
                    setFamiliesList([])

                    return
                }

                if (response?.isSuccess) {
                    const modifiedFamiliesList = (response?.families ?? []).map((obj: FamilyObj) => ({
                        ...obj,
                        label: `${obj?.family_name ?? ""} ${obj?.family_id ? `(${obj.family_id})` : ''}`,
                        value: obj?.family_id
                    }))

                    setFamiliesList(modifiedFamiliesList)
                }
            } else {
                setFamiliesList([])
            }
        } catch (error) {
            console.log(error)
        }
    }

    const fetchFamilyFeeSummary = async ({
        selectedYear,
        selectedQuarter,
        selectedFamilyId,
        selectedCurrentPage,
        selectedRowsPerPage
    }: {
        selectedYear: string;
        selectedQuarter: string;
        selectedFamilyId: string | null;
        selectedCurrentPage: number;
        selectedRowsPerPage: number;
    }): Promise<boolean> => {

        const payload = {
            year: Number(selectedYear ?? ""),
            quarter: selectedQuarter,
            familyId: selectedFamilyId,
            currentPage: selectedCurrentPage,
            rowsPerPage: selectedRowsPerPage
        }

        const response = await (dispatch(getFamilyFeeSummary(payload)) as unknown as Promise<GetFamilyFeeSummaryResponseBody>)

        if (!response) {
            setTableData([])
            setTablePaginationObj({
                currentPage: 0,
                rowsPerPage: 5,
                totalRows: 0
            });

            return false
        }

        if (!response?.isSuccess) {
            dispatch(showError(response?.message || "Data not found"))
            setTableData([])
            setTablePaginationObj({
                currentPage: 0,
                rowsPerPage: 5,
                totalRows: 0
            });

            return false
        }

        if (response.isSuccess) {
            setTableData(response?.data?.familyFeeSummary ?? [])
            setTablePaginationObj(prevState => ({
                ...prevState,
                currentPage: selectedCurrentPage - 1,
                rowsPerPage: selectedRowsPerPage,
                totalRows: response?.count ?? 0
            }))
        }

        return true
    }

    const handleTableRowsPerPageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        try {
            if (!filterFormikRef?.current) {
                return
            }

            const { values } = filterFormikRef.current

            setLoadingStatus("fetchTableDataLoading")

            await fetchFamilyFeeSummary({
                selectedYear: values?.year ?? "",
                selectedQuarter: values?.quarter ?? "",
                selectedFamilyId: values?.family?.family_id ?? null,
                selectedCurrentPage: 1,
                selectedRowsPerPage: Number(event.target.value)
            })
        } catch (error) {
            console.log(error)
        } finally {
            setLoadingStatus("")
        }
    };

    const handleTablePageChange = async (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        try {
            if (!filterFormikRef?.current) {
                return
            }

            const { values } = filterFormikRef.current

            setLoadingStatus("fetchTableDataLoading")

            await fetchFamilyFeeSummary({
                selectedYear: values?.year ?? "",
                selectedQuarter: values?.quarter ?? "",
                selectedFamilyId: values?.family?.family_id ?? null,
                selectedCurrentPage: page + 1,
                selectedRowsPerPage: tablePaginationObj.rowsPerPage
            })
        } catch (error) {
            console.log(error)
        } finally {
            setLoadingStatus("")
        }
    };

    const handleApplyFilters = async (values: FilterValues) => {
        try {
            setLoadingStatus("appliedFilterLoading")

            const isSuccess = await fetchFamilyFeeSummary({
                selectedYear: values?.year ?? "",
                selectedQuarter: values?.quarter ?? "",
                selectedFamilyId: values?.family?.family_id ?? null,
                selectedCurrentPage: 1,
                selectedRowsPerPage: tablePaginationObj.rowsPerPage,
            })

            if (isSuccess) {
                setAreFiltersApplied(true)
            }
        } catch (error) {
            console.log(error)
        } finally {
            setLoadingStatus("")
        }
    };

    return (
        <CommonLayout>
            <>
                <Box sx={{ marginBottom: "0.5rem" }}>
                    <BreadCrum array={routerPath} />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", lg: "row" },
                        justifyContent: { xs: "flex-start", lg: "space-between" },
                        alignItems: { xs: "stretch", lg: "flex-start" },
                        columnGap: "1.5rem",
                        rowGap: { xs: "1.5rem", lg: 0 },
                        marginBottom: "2rem",
                        maxWidth: "100%",
                        minWidth: 0,
                    }}
                >
                    <Typography
                        variant="h1"
                        sx={{
                            color: theme.palette.text.primary,
                            flexShrink: 0,
                            marginTop: "10px"
                        }}
                    >
                        RIA Summary
                    </Typography>
                    <Box sx={{ width: { xs: "100%", lg: "auto" }, minWidth: 0, maxWidth: "100%" }}>
                        <Formik
                            innerRef={filterFormikRef}
                            validationSchema={filterValidationSchema}
                            initialValues={initialFilterValues}
                            onSubmit={handleApplyFilters}
                        >
                            {({ values }) => {
                                const quarterOptions = getQuartersForFiscalYear(values?.year ?? "")

                                return (
                                    <Form style={{ width: "100%", maxWidth: "100%" }}>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: { xs: "column", lg: "row" },
                                                flexWrap: "nowrap",
                                                alignItems: { xs: "stretch", lg: "flex-start" },
                                                justifyContent: { xs: "flex-start", lg: "flex-end" },
                                                columnGap: { xs: 0, lg: "1rem" },
                                                width: "100%",
                                                maxWidth: "100%",
                                                minWidth: 0,
                                            }}
                                        >
                                            <Box sx={filterFieldWrapperSx}>
                                                <MFSelectField
                                                    name='year'
                                                    label="Year *"
                                                    items={YEARS}
                                                    onChange={handleYearChange}
                                                    disabled={["tableDataLoading", "appliedFilterLoading"].includes(loadingStatus)}
                                                />
                                            </Box>
                                            <Box sx={filterQuarterWrapperSx}>
                                                <MFSelectField
                                                    name='quarter'
                                                    label="Quarter *"
                                                    items={quarterOptions}
                                                    disabled={["tableDataLoading", "appliedFilterLoading"].includes(loadingStatus)}
                                                />
                                            </Box>
                                            <Box sx={filterFamilyWrapperSx}>
                                                <FormikSearchDropdown
                                                    name="family"
                                                    label="Family *"
                                                    options={familiesList ?? []}
                                                    fetchData={fetchFamiliesData}
                                                    filterOptions={(x) => x}
                                                    onOpen={() => {
                                                        if (!filterFormikRef?.current?.values?.family) {
                                                            setFamiliesList([]);
                                                        }
                                                    }}
                                                    disabled={["tableDataLoading", "appliedFilterLoading"].includes(loadingStatus)}
                                                />
                                            </Box>
                                            <Box sx={filterSubmitWrapperSx}>
                                                <MFSubmitButton
                                                    label="Apply Filters"
                                                    fullWidth={false}
                                                    sx={filterSubmitButtonSx(theme)}
                                                    loadingEnable={["appliedFilterLoading"].includes(loadingStatus)}
                                                    disabled={["tableDataLoading"].includes(loadingStatus)}
                                                    loadingPosition="center"
                                                />
                                            </Box>
                                        </Box>
                                    </Form>
                                )
                            }}
                        </Formik>
                    </Box>
                </Box>
                <Box
                    sx={{
                        padding: '25px',
                        borderRadius: '15px',
                        background: theme.palette.common.white,
                        boxShadow: `0px 4px 10px 0px ${theme.palette.common.black}0D`
                    }}
                >
                    {!areFiltersApplied && <NoData />}
                    {areFiltersApplied && (
                        <RiaSummaryTable
                            isLoading={["appliedFilterLoading", "fetchTableDataLoading"].includes(loadingStatus)}
                            tableData={tableData}
                            tablePaginationObj={tablePaginationObj}
                            handleTableRowsPerPageChange={handleTableRowsPerPageChange}
                            handleTablePageChange={handleTablePageChange}
                        />
                    )}
                </Box>
            </>
        </CommonLayout>
    )
}
